import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';
/* eslint-disable consistent-return */
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  createOffer,
  getAddress,
  getAllAvantages,
  getContractTypes,
  getDegreeLevels,
  getJobs,
  getLangOffre,
} from '@webapps/business';
import {
  FormikAutocomplete,
  FormikAutocompletePx,
  FormikDatePicker,
  FormikTextField,
  TextField,
  TextFieldPx,
  useAuth,
  useLocalStorage,
  useMutation,
  useQuery,
  useToast,
} from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

const CreateAnnonce = ({ onChangeStepModal, onClose, refetchOffre, idBusinessExternal }) => {
  const { idUser } = useAuth();
  const [data, setData] = useState({
    job: {
      ogr: '',
      sector: {
        id: '',
      }
    },
    ...(idBusinessExternal 
      ? { businessExternal: { id: idBusinessExternal } }
      : { business: { id: idUser } }
    ),
    degreeLevel: {
      id: '',
    },
    description: '',
    contractType: {
      id: '',
    },
    address: {
      label: '',
      cityCode: '',
      city: '',
    },
    contractLength: 0,
    contractLengthMax: 0,
    renewable: false,
    workArrangement: 'Temps plein',
    telework: '',
    salary: '',
    salaryMax: '',
    jobAdvantages: [],
    transport: false,
    drivingLicence: '',
    createdAt: '',
    lastUpdateDate: '',
    publicationDate: new Date(),
    deadLine: new Date(),
    workedLangage: [],
  });
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    onChangeStepModal(1);
  }, []);

  const {
    error,
    isLoading: isLoadingCreateOffer,
    isSuccess,
    mutateAsync: mutateCreateOffer,
  } = useMutation(createOffer);

  const { addToast } = useToast();

  useEffect(() => {
    if (error) {
      addToast({
        status: 'error',
        message: 'Erreur pour publier une annonce',
      });
    }
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Annonce publiée avec succès',
      });
      onClose();
      refetchOffre();
    }
  }, [error, isSuccess]);

  const makeRequest = async ({ deadLine, jobAdvantages, ...values }) => {
    await mutateCreateOffer({
      data: {
        jobAdvantages: jobAdvantages?.map((str) => ({ id: str })),
        deadLine: format(new Date(deadLine), "yyyy-MM-dd'T'HH:mm:ss"),
        ...values,
      },
    });
  };

  const handleNextStep = (newData, final = false) => {
    setData((prev) => ({ ...prev, ...newData }));

    if (final) {
      makeRequest(newData);
      return;
    }

    setCurrentStep((prev) => prev + 1);
    onChangeStepModal((prev) => prev + 1);
  };

  const handlePrevStep = (newData) => {
    setData((prev) => ({ ...prev, ...newData }));
    setCurrentStep((prev) => prev - 1);
    onChangeStepModal((prev) => prev - 1);
  };

  const steps = [
    <StepOne key={1} data={data} next={handleNextStep} />,
    <StepTwo key={2} data={data} next={handleNextStep} prev={handlePrevStep} />,
    <StepThree
      key={3}
      data={data}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
    <StepFour
      key={4}
      data={data}
      isLoading={isLoadingCreateOffer}
      next={handleNextStep}
      prev={handlePrevStep}
    />,
  ];

  return steps[currentStep];
};

CreateAnnonce.propTypes = {
  onClose: PropTypes.func,
  refetchOffre: PropTypes.func,
  onChangeStepModal: PropTypes.func,
};

const stepOneValidationSchema = Yup.object({
  job: Yup.object()
    .shape({
      ogr: Yup.string().required('Requis'),
    })
    .required('Requis'),
  degreeLevel: Yup.object().shape({
    id: Yup.string().required('Requis'),
  }),
  contractType: Yup.object().shape({
    id: Yup.string().required('Requis'),
  }),
  address: Yup.object()
    .shape({
      label: Yup.string().required('Requis'),
      city: Yup.string().required('Requis'),
      cityCode: Yup.string().required('Requis'),
    })
    .required('Requis'),
});

const StepOne = ({ data, next }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  const { data: degreeLevels = [] } = useQuery(getDegreeLevels, {
    key: 'degreeLevels',
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [keywordRequest, setKeywordRequest, removeKeywordRequest] =
    useLocalStorage('jobsKeyword');

  const { data: { content: jobs = [] } = {} } = useQuery(getJobs, {
    key: ['jobs', keywordRequest],
    variables: { keyword: keywordRequest || '' },
  });

  const { data: contractTypes = [] } = useQuery(getContractTypes, {
    key: 'contractTypes',
  });

  const [addresses, setAddresses] = useState([]);

  const getAdresses = useMutation(getAddress, {
    onSuccess: (result) => {
      setAddresses(result?.data?.features);
    },
  });

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepOneValidationSchema,
    onSubmit: async (values) => {
      const selectedJob = jobs.find((job) => job.ogr === values.job.ogr);
      if (selectedJob) {
        values.job.sector.id = selectedJob.sector.id;
        await next(values);
      } else {
        console.error('Aucun travail correspondant trouvé pour le travail sélectionné.');
      }
    },
  });

  const getAllInfoAdresses = useMutation(getAddress, {
    onSuccess: (result) => {
      formik.setFieldValue(
        'address.label',
        result?.data?.features[0]?.properties?.label
      );
      formik.setFieldValue(
        'address.city',
        result?.data?.features[0]?.properties?.city
      );
      formik.setFieldValue(
        'address.cityCode',
        result?.data?.features[0]?.properties?.postcode
      );
    },
  });

  const changeAddress = async (value) => {
    await getAllInfoAdresses.mutateAsync({ name: value || 'test' });
  };

  // const selectJobs = async () => {
  //   formik.setFieldValue('job.ogr', ogrJob);
  // };

  const handleChangeAddress = async (searchValue) => {
    if (!searchValue.target.value) {
      return null;
    }
    await getAdresses.mutateAsync({ name: searchValue.target.value });
  };

  // const handleSearchJobs = (searchValue) => {
  //   if (!searchValue.target.value) {
  //     return null;
  //   }
  //   setKeywordRequest(searchValue.target.value);
  //   refetchJobs({ keyword: searchValue.target.value });
  // };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12} sm={9}>
        <Grid container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              defaultValue=""
              formik={formik}
              borderRadius={6}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => value.ogr === option.ogr}
              label="Indiquer un intitulé de poste"
              name="job.ogr"
              onInputChange={(event, value) => {
                setKeywordRequest(value);
              }}
              options={jobs}
              pathValue="ogr"
              value={
                jobs.find(({ ogr }) => ogr === formik.values.job.ogr) || null
              }
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(2) })} container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              defaultValue=""
              formik={formik}
              borderRadius={6}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => value.id === option.id}
              label="Niveau d'étude"
              name="degreeLevel.id"
              options={degreeLevels}
              pathValue="id"
              value={
                degreeLevels.find(
                  ({ id }) => id === formik.values.degreeLevel.id
                ) || null
              }
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(2) })} container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              defaultValue=""
              formik={formik}
              borderRadius={6}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => value.id === option.id}
              label="Type de contrat"
              name="contractType.id"
              options={contractTypes}
              pathValue="id"
              value={
                contractTypes.find(
                  ({ id }) => id === formik.values.contractType.id
                ) || null
              }
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(2) })} container>
          <Grid item xs={12}>
            <Autocomplete
              autoComplete={false}
              // defaultValue=""
              error={
                !!formik.errors.address?.label && !!formik.errors.address?.label
              }
              getOptionLabel={(option) => option || ''}
              helperText={
                formik.errors.address?.label && formik.errors.address?.label
              }
              onInputChange={(event, value) => {
                changeAddress(value);
              }}
              options={addresses?.map((option) => option?.properties?.label)}
              renderInput={(params) => (
                <TextFieldPx
                  {...params}
                  borderRadius={6}
                  error={
                    !!formik.errors.address?.label && !!formik.errors.address?.label
                  }
                  helperText={
                    formik.errors.address?.label && formik.errors.address?.label
                  }
                  // defaultValue=""
                  label="Adresse"
                  name="address.label"
                  onChange={(value) => {
                    handleChangeAddress(value);
                  }}
                  placeholder="N°rue code postal"
                  value={formik.values.address.label}
                  variant="outlined"
                />
              )}
              size="small"
            // value={addresses.filter(({ ogr }) => ogr === formik.values.job.ogr)[0]}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({ textAlign: 'center', marginTop: spacing(4) })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepOne.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
};

const stepTwoValidationSchema = Yup.object({
  contractLength: Yup.number().required('Requis'),
  workArrangement: Yup.string().required('Requis'),
  telework: Yup.string().required('Requis'),
  salary: Yup.string().required('Requis'),
  jobAdvantages: Yup.array(),
});

const DUREE_TRAVAIL = ['Temps plein'];
const TELETRAVAIL = [
  {
    label: 'Présentiel',
  },
  {
    label: 'Distanciel',
  },
  {
    label: 'Présentiel et Distanciel',
  },
];

const StepTwo = ({ data, next, prev }) => {
  const {
    css,
    cx,
    global,
    theme: { spacing },
  } = useStyles();

  const { data: avantages = [], isLoading } = useQuery(getAllAvantages, {
    key: 'allAvantages',
  });

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepTwoValidationSchema,
    onSubmit: (values) => next(values),
  });

  const handleChangeAvantages = async (id) => {
    if (formik.values.jobAdvantages.find((item) => item === id)) {
      await formik.setFieldValue(
        'jobAdvantages',
        formik.values.jobAdvantages.filter((item) => item !== id)
      );
    } else {
      await formik.setFieldValue('jobAdvantages', [
        ...formik.values.jobAdvantages,
        id,
      ]);
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12} sm={9}>
        <Grid container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Durée du contrat (en mois)"
              name="contractLength"
              type="number"
            />
          </Grid>
        </Grid>
        {/*
        <Grid container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              fullWidth
              label="Durée du contrat max. (en mois)"
              name="contractLengthMax"
              type="number"
            />
          </Grid>
    </Grid>*/}

        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Présence"
              name="telework"
              select
              value={TELETRAVAIL.find(({ label }) => label === formik.values.telework)?.label || ''}
            >
              {TELETRAVAIL.map(({ label }, index) => (
                <MenuItem key={index} value={label}>
                  {label}
                </MenuItem>
              ))}
            </FormikTextField>
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(2) })} container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Salaire brut (€/mois)"
              name="salary"
              type="number"
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(2.5) })} container>
          <FormLabel style={{ color: '#152652', marginBottom: spacing(1.5) }}>Avantages</FormLabel>
          <FormGroup row>
            {isLoading ? (
              <CircularProgress />
            ) : (
              avantages?.map(({ id, label }) => (
                <FormControlLabel
                  key={id}
                  classes={{
                    label: cx(global.title),
                  }}
                  control={
                    <Checkbox
                      className={css({ '.MuiIconButton-label': { color: '#152652 !important' } })}
                      checked={
                        !!formik.values.jobAdvantages.find(
                          (item) => item === id
                        )
                      }
                      name="jobAdvantages"
                      onChange={() => handleChangeAvantages(id)}
                      size="small"
                    />
                  }
                  label={label}
                  size="small"
                />
              ))
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepTwo.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
};

const VEHICULE = [
  {
    label: 'Oui',
    value: true,
  },
  {
    label: 'Non',
    value: false,
  },
];

const PROFIL_STATUS = [
  {
    label: 'Oui',
    value: true,
  },
  {
    label: 'Non',
    value: false,
  },
];

const PERMIS = [
  {
    label: 'Pas de permis',
    value: 'non',
  },
  {
    value: 'AM',
    label: 'AM',
  },
  {
    value: 'A',
    label: 'A',
  },
  {
    value: 'A1',
    label: 'A1',
  },
  {
    value: 'A2',
    label: 'A2',
  },
  {
    value: 'B',
    label: 'B',
  },
  {
    value: 'B1',
    label: 'B1',
  },
  {
    value: 'BE',
    label: 'BE',
  },
  {
    value: 'C',
    label: 'C',
  },
  {
    value: 'C1',
    label: 'C1',
  },
  {
    value: 'C1E',
    label: 'C1E',
  },
  {
    value: 'D',
    label: 'D',
  },
  {
    value: 'D1',
    label: 'D1',
  },
  {
    value: 'D1E',
    label: 'D1E',
  },
  {
    value: 'DE',
    label: 'DE',
  },
  {
    value: 'R482',
    label: 'R482',
  },
  {
    value: 'R483',
    label: 'R483',
  },
  {
    value: 'R484',
    label: 'R484',
  },
  {
    value: 'R485',
    label: 'R485',
  },
  {
    value: 'R486',
    label: 'R486',
  },
  {
    value: 'R487',
    label: 'R487',
  },
  {
    value: 'R489',
    label: 'R489',
  },
  {
    value: 'R490',
    label: 'R490',
  },
];

const stepThreeValidationSchema = Yup.object({
  transport: Yup.bool().required('Requis'),
  drivingLicence: Yup.string().required('Requis'),
  workedLangage: Yup.array().required('Requis'),
  deadLine: Yup.string().required('Requis'),
});

const StepThree = ({ data, next, prev }) => {
  const {
    css,
    theme: { spacing },
  } = useStyles();

  const { data: languages = [], isLoading: langLoading } = useQuery(
    getLangOffre,
    {
      key: 'languagesOffre',
    }
  );

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepThreeValidationSchema,
    onSubmit: (values) => next(values),
  });

  const today = new Date();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid container xs={12} sm={9}>
        <Grid container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Etre véhiculé"
              name="transport"
              select
            >
              {VEHICULE.map(({ label, value }, index) => (
                <MenuItem key={index} value={value}>
                  {label}
                </MenuItem>
              ))}
            </FormikTextField>
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikTextField
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Permis de conduire"
              name="drivingLicence"
              select
            >
              {PERMIS.map(({ label, value }, index) => (
                <MenuItem key={index} value={value}>
                  {label}
                </MenuItem>
              ))}
            </FormikTextField>
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikAutocompletePx
              formik={formik}
              borderRadius={6}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              label="Langue(s) requise(s)"
              loading={langLoading}
              loadingText="En chargement"
              multiple
              name="workedLangage"
              options={languages}
              value={formik.values.workedLangage}
            />
          </Grid>
        </Grid>
        <Grid className={css({ marginTop: spacing(3) })} container>
          <Grid item xs={12}>
            <FormikDatePicker
              formik={formik}
              borderRadius={6}
              fullWidth
              label="Date d'expiration de l'offre"
              minDate={today}
              name="deadLine"
              variant='outlined'
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

StepThree.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
};

const stepFourValidationSchema = Yup.object({
  description: Yup.string().required('Requis'),
});

const StepFour = ({ data, isLoading, next, prev }) => {
  const {
    classes,
    css,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const formik = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: stepFourValidationSchema,
    onSubmit: (values) => {
      next(values, true);
    },
  });

  const [count, setCount] = React.useState(0);
  const [textarea, setTextarea] = React.useState(formik.values.description);

  const [countAspect, setCountAspect] = React.useState('countAspect');
  const countAspectRef = React.useRef();
  countAspectRef.current = countAspect;

  const handleSubmit = () => {
    if (!textarea) {
      formik.values.description = ' ';
    } else {
      formik.values.description = textarea;
    }
    formik.handleSubmit();
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        '@media (max-width:768px)': {
          margin: spacing(0),
          marginTop: spacing(1)
        },
      })}
    >
      <div className={css({ width: '100%' })}>
        <CKEditor
          className={css({
            width: '100%',
            resize: 'none'
          })}
          config={{
            wordCount: {
              onUpdate: (stats) => {
                setCount(stats.characters);
              },
            },
            toolbar: [
              'numberedList',
              'bulletedList',
            ],
          }}
          data={textarea}
          editor={Editor}
          onChange={(event, editor) => {
            setTextarea(editor.getData());
            if (count > 3000) {
              setCountAspect('countAspectPassed');
            } else {
              setCountAspect('countAspect');
            }
          }}
        />
        <div style={{ marginTop: spacing(2) }} className={cx(classes[countAspectRef.current], global.infoText)}>{count} / 3000</div>
      </div>
      <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            onClick={() => prev(formik.values)}
            variant="outlined"
          >
            Retour
          </Button>
        </Grid>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          {countAspectRef.current === 'countAspect' ? (
            <Button
              className={css({ borderRadius: 400 })}
              color="primary"
              disabled={isLoading}
              fullWidth
              onClick={handleSubmit}
              type="submit"
              variant="contained"
            >
              Créer
            </Button>
          ) : (
            <Button
              className={css({ borderRadius: 400 })}
              color="primary"
              disabled
              fullWidth
              type="submit"
              variant="contained"
            >
              Créer
            </Button>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const styles = () => ({
  countAspect: {
    color: 'black',
  },
  countAspectPassed: {
    color: 'red',
  }
});


StepFour.propTypes = {
  data: PropTypes.object,
  next: PropTypes.func,
  prev: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default CreateAnnonce;
